import { useCallback, useMemo } from 'react';
import { rage } from '~/rage';
import { useObservable } from '~/rage/reactHooks';
import { RewardType } from '~/shared';

const useDonateItemNumberFormat = () => {
	const formatNumber = useCallback((number: number | string): string => {
		const value = typeof number === 'string' ? number : number.toString();
		return value.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
	}, []);
	const parseFormattedNumber = useCallback(
		(value: string): string => value.replace(/\s/g, ''),
		[],
	);
	const filterNumericOnly = useCallback(
		(value: string): string => value.replace(/\D/g, ''),
		[],
	);
	return { formatNumber, parseFormattedNumber, filterNumericOnly };
};

const useDonateItemRewardOptions = () => {
	const vehicleModels = useObservable(rage.vehicles.getVehicleModels());
	const lootboxes = useObservable(rage.cases.getLootboxes());
	const bundles = useObservable(rage.cases.getBundles());
	const inventoryItems = useObservable(rage.inventory.getInventoryItems());
	const gameConfigs = useObservable(rage.gameConfig.getConfigs());

	const vehicleOptions = useMemo(
		() =>
			vehicleModels?.map((vehicle) => ({
				value: vehicle.model,
				label: vehicle.name,
			})) || [],
		[vehicleModels],
	);
	const lootboxOptions = useMemo(
		() =>
			lootboxes?.map((lootbox) => ({
				value: lootbox.id,
				label: lootbox.name,
			})) || [],
		[lootboxes],
	);
	const bundleOptions = useMemo(
		() =>
			bundles?.map((bundle) => ({ value: bundle.id, label: bundle.name })) ||
			[],
		[bundles],
	);
	const itemOptions = useMemo(
		() =>
			inventoryItems?.map((item) => ({ value: item.id, label: item.name })) ||
			[],
		[inventoryItems],
	);

	const getTuningKitOptions = useCallback(
		(vehicleModel: string) =>
			gameConfigs?.vehicleTuning?.itemsKits
				.filter(
					(kit) =>
						!kit.id.startsWith('stock_') &&
						(kit.vehicleModel === null || kit.vehicleModel === vehicleModel),
				)
				.map((kit) => ({ value: kit.id, label: kit.name })) || [],
		[gameConfigs],
	);

	const getTuningItemOptions = useCallback(
		(vehicleModel: string) =>
			gameConfigs?.vehicleTuning?.items
				.filter(
					(item) =>
						!item.id.startsWith('stock_') &&
						(item.vehicleModel === null || item.vehicleModel === vehicleModel),
				)
				.map((item) => ({
					value: item.id,
					label: item.name,
					category: item.shopCategory ?? undefined,
				})) || [],
		[gameConfigs],
	);

	const tuningKitAllOptions = useMemo(
		() =>
			gameConfigs?.vehicleTuning?.itemsKits
				.filter((kit) => !kit.id.startsWith('stock_'))
				.map((kit) => ({ value: kit.id, label: kit.name })) || [],
		[gameConfigs],
	);
	const tuningItemAllOptions = useMemo(
		() =>
			gameConfigs?.vehicleTuning?.items
				.filter((item) => !item.id.startsWith('stock_'))
				.map((item) => ({ value: item.id, label: item.name })) || [],
		[gameConfigs],
	);

	const getOptionsForRewardType = useCallback(
		(type: RewardType) => {
			switch (type) {
				case RewardType.VEHICLE:
					return vehicleOptions;
				case RewardType.LOOTBOX:
					return lootboxOptions;
				case RewardType.BUNDLE:
					return bundleOptions;
				case RewardType.ITEM:
					return itemOptions;
				case RewardType.VEHICLE_TUNING_KIT:
					return tuningKitAllOptions;
				case RewardType.VEHICLE_TUNING_ITEM:
					return tuningItemAllOptions;
				default:
					return [];
			}
		},
		[
			bundleOptions,
			itemOptions,
			lootboxOptions,
			tuningItemAllOptions,
			tuningKitAllOptions,
			vehicleOptions,
		],
	);

	return { getOptionsForRewardType, getTuningItemOptions, getTuningKitOptions };
};

export { useDonateItemNumberFormat, useDonateItemRewardOptions };
